import { ISaSmsMigration } from '@interfaces/sa-sms';
import { Form, Input, Modal, notification, Typography } from 'antd';
import { useSaSmsGet, useSaSmsWrite } from 'hooks/use-sa-sms-api';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

type AttachProps = { migration: ISaSmsMigration | null; onClose: () => void; onDone: () => void };

/** Attach the migrated number by its Twilio number SID (marketing-api verifies it exists in the tenant subaccount). */
export const SmsMigrationAttachModal: React.FC<AttachProps> = ({ migration, onClose, onDone }) => {
    const { t } = useTranslation(['sa-sms']);
    const { send } = useSaSmsWrite();
    const [form] = Form.useForm<{ phoneNumberSid: string }>();
    const [busy, setBusy] = useState(false);

    const attach = async () => {
        const { phoneNumberSid } = await form.validateFields();
        if (!migration) return;
        setBusy(true);
        try {
            await send('post', `/migrations/${migration.id}/attach`, {
                phoneNumberSid: phoneNumberSid.trim(),
            });
            notification.success({ message: t('migrations.attached') });
            onClose();
            onDone();
        } catch {
            // already notified by the write hook; stay open for a retry
        } finally {
            setBusy(false);
        }
    };

    return (
        <Modal
            open={!!migration}
            title={t('migrations.attach')}
            okText={t('migrations.attach')}
            cancelText={t('actions.cancel')}
            confirmLoading={busy}
            onOk={attach}
            onCancel={onClose}
            maskClosable={false}
            destroyOnClose
            afterClose={() => form.resetFields()}
        >
            <p>{t('migrations.attachHelp', { number: migration?.phoneE164 })}</p>
            <Form form={form} layout="vertical">
                <Form.Item
                    name="phoneNumberSid"
                    label={t('migrations.sid')}
                    rules={[
                        { required: true },
                        { pattern: /^PN[0-9a-fA-F]{32}$/, message: t('migrations.sidFormat') },
                    ]}
                >
                    <Input placeholder="PNxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx" />
                </Form.Item>
            </Form>
        </Modal>
    );
};

/** Short-lived (5 min) signed links to the private bill / LOA documents of a request. */
export const SmsMigrationDocumentsModal: React.FC<{
    migrationId: string | null;
    onClose: () => void;
}> = ({ migrationId, onClose }) => {
    const { t } = useTranslation(['sa-sms']);
    const docs = useSaSmsGet<{ links: string[] }>(
        `/migrations/${migrationId}/documents`,
        undefined,
        !!migrationId,
    );
    return (
        <Modal
            open={!!migrationId}
            title={t('migrations.documents')}
            footer={null}
            onCancel={onClose}
        >
            {(docs.data?.links ?? []).map((href, i) => (
                <p key={href}>
                    <a href={href} target="_blank" rel="noopener noreferrer">
                        {t('migrations.document', { n: i + 1 })}
                    </a>
                </p>
            ))}
            <Typography.Text type="secondary">{t('migrations.linksExpire')}</Typography.Text>
        </Modal>
    );
};
